import { DialogBody, FormField, Input, Select } from '@/shared/components';
import type {
  CleaningTeamMemberRequest,
  CompanyResponse,
  PropertyGroupResponse,
  UserResponse,
} from '@/shared/api';
import { CleaningTeamMembers } from './CleaningTeamMembers';
import { CleaningTeamPropertyGroups } from './CleaningTeamPropertyGroups';
import type { CleaningTeamFormValues } from './CleaningTeamEditorDialog';

interface Props {
  values: CleaningTeamFormValues;
  busy: boolean;
  companies: CompanyResponse[];
  users: UserResponse[];
  propertyGroups: PropertyGroupResponse[];
  onNameChange: (name: string) => void;
  onCompanyChange: (companyId: string | null) => void;
  onActiveChange: (isActive: boolean) => void;
  onToggleMember: (userId: string) => void;
  onMemberRoleChange: (userId: string, role: CleaningTeamMemberRequest['role']) => void;
  onTogglePropertyGroup: (id: string) => void;
}

/** Quellen werden bearbeitet; die effektive Apartmentreichweite kommt immer vom Server. */
export function CleaningTeamFormFields({
  values,
  busy,
  companies,
  users,
  propertyGroups,
  onNameChange,
  onCompanyChange,
  onActiveChange,
  onToggleMember,
  onMemberRoleChange,
  onTogglePropertyGroup,
}: Props) {
  return (
    <DialogBody className="space-y-5">
      <FormField label="Teamname" required>
        <Input
          value={values.name}
          onChange={(event) => onNameChange(event.target.value)}
          autoFocus
        />
      </FormField>
      <FormField
        label="Reinigungsfirma"
        hint="Organisationsmetadatum, keine zusätzliche Rechtequelle."
      >
        <Select
          value={values.companyId ?? ''}
          onChange={(event) => onCompanyChange(event.target.value || null)}
        >
          <option value="">— keine —</option>
          {companies.map((company) => (
            <option key={company.id} value={company.id}>
              {company.name}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField label="Status">
        <Select
          value={values.isActive ? 'active' : 'inactive'}
          onChange={(event) => onActiveChange(event.target.value === 'active')}
        >
          <option value="active">Aktiv</option>
          <option value="inactive">Inaktiv — operativer Scope entzogen</option>
        </Select>
      </FormField>
      <section className="border-line/50 space-y-3 rounded-md border p-4">
        <p className="text-ink text-sm font-medium">Mitglieder und Leitungen</p>
        {busy ? (
          <p className="text-ink-faint text-sm">Wird geladen…</p>
        ) : (
          <CleaningTeamMembers
            users={users}
            members={values.members}
            onToggle={onToggleMember}
            onRoleChange={onMemberRoleChange}
          />
        )}
      </section>
      <section className="border-line/50 space-y-3 rounded-md border p-4">
        <p className="text-ink text-sm font-medium">Objektfreigaben</p>
        <p className="text-ink-soft text-sm">
          Der Server löst daraus die wirksamen Apartments auf; die Benutzerakte zeigt anschließend
          deren Quellen.
        </p>
        {!busy && (
          <CleaningTeamPropertyGroups
            groups={propertyGroups}
            selectedIds={values.propertyGroupIds}
            onToggle={onTogglePropertyGroup}
          />
        )}
      </section>
    </DialogBody>
  );
}
